import React, { Component, ReactNode } from 'react'

import { flatten, get } from 'lodash'
import Reserve from './Reserve'

import Alert from '../../../Animated/Alert'
import { Spinner } from '@ui'
import { DateUtil, TIME, closest15minutes } from '@utils'
import { ApolloError } from 'apollo-client'
import api from '@api'
import { add, differenceInMilliseconds, isAfter, isBefore, parseISO } from 'date-fns'

export const TimeFormat = 'YYYY-MM-DD HH:mm'

interface CardAvailabilityInfoProps {
  status: any
  forTime?: number
  inTime?: number
  width?: number
  capitalize?: boolean
}

export const CardAvailabilityInfo: React.FC<CardAvailabilityInfoProps> = ({
  status,
  forTime,
  inTime,
  width = 152,
  capitalize = true,
}) => {
  return (
    <div
      style={{
        fontSize: 12,
        textTransform: capitalize ? 'capitalize' : 'unset',
        padding: '5px 10px',
        marginBottom: 0,
        marginLeft: 20,
        width,
      }}>
      <i
        className={`fa fa-${status === 'available' ? 'check-circle' : 'exclamation-circle'}`}
        style={{ marginRight: 10 }}
      />
      {status}
      {forTime && forTime > 0 && ` for the next ${Math.ceil(forTime / 60000)} minutes`}
      {inTime && inTime > 0 && ` in ${Math.ceil(inTime / 60000)} minutes`}
    </div>
  )
}

export default ({
  id,
  device,
  addUserBookingSuccess,
}: {
  id: string
  device: any
  addUserBookingSuccess: () => void
}) => {
  return (
    <Data ids={[id]} device={device} addUserBookingSuccess={addUserBookingSuccess}>
      {({ data, createEvent }: { data: any; createEvent: (t: number) => void }) => {
        if (data.isFetching) {
          return (
            <Alert
              type={data.status === 'available' ? 'success' : 'warning'}
              style={{
                fontSize: 12,
                textTransform: 'capitalize',
                padding: '5px 10px',
                marginBottom: 0,
                marginLeft: 20,
                width: 152,
              }}>
              <span style={{ marginRight: 10 }}>
                <Spinner size={10} />
              </span>
              Checking Availability
            </Alert>
          )
        } else if (data.availability) {
          const avail = get(data, ['availability', id], {})
          return (
            <div>
              <CardAvailabilityInfo status={avail.status} forTime={avail.for} inTime={avail.in} />
              {avail.status === 'available' && (
                <Reserve reserve={(minutes: number) => createEvent(minutes)} />
              )}
            </div>
          )
        }
        return null
      }}
    </Data>
  )
}

interface DataProps {
  ids: string[]
  device: { name: string } | null // Adjust the type according to your needs
  addUserBookingSuccess: (event: any) => void // Adjust the type according to your needs
  children: any
}

interface DataState {
  status: any
  isFetching: boolean
  availability?: Record<string, boolean> // Adjust the type according to your needs
  error?: ApolloError
}

export class Data extends Component<DataProps, DataState> {
  state: DataState = {
    status: null,
    isFetching: false,
  }

  createEvent = (minutes: number): void => {
    const { ids, device, addUserBookingSuccess } = this.props

    const startBeforeFormat = this.state.availability?.in
      ? DateUtil()
          .utc()
          .add(this.state.availability.in as unknown as number, TIME.MILLISECONDS)
      : closest15minutes(DateUtil()).utc()

    const start = startBeforeFormat.format(TimeFormat)

    const end = startBeforeFormat.clone().add(minutes, TIME.MINUTES).format(TimeFormat)

    let summary
    if (device && device.name) summary = `${device.name} reserved room`

    api.meetingRoom
      .createCalendarEvent({
        spaceId: ids[0],
        start,
        end,
        summary,
      })
      .then((event: any) => {
        if (!event) return this.setState({ status: null, isFetching: false })
        this.checkAvailability()

        addUserBookingSuccess(event)
      })
      .catch((error: ApolloError) => {
        this.setState({ error })
      })

    this.setState({ isFetching: true })
  }

  checkAvailability = (): void => {
    const { ids } = this.props
    const from = DateUtil().utc().sub(2, TIME.HOURS).format(TimeFormat)
    const to = DateUtil().utc().add(2, TIME.HOURS).format(TimeFormat)

    api.meetingRoom
      .calendars({ ids, from, to })
      .then((calendars: any) => {
        if (!calendars.find((c: any) => c.calendar))
          return this.setState({ status: null, isFetching: false })

        const availability: any = {}
        for (const calendar of calendars) {
          if (!calendar.calendar) continue
          availability[calendar.id] = isAvailable(get(calendar, 'calendar.calendarEvents', []))
        }

        this.setState({
          availability,
          isFetching: false,
        })
      })
      .catch((error: ApolloError) => {
        this.setState({ status: null, isFetching: false, error })
      })

    this.setState({ isFetching: true })
  }

  componentDidMount() {
    this.checkAvailability()
  }

  render(): ReactNode {
    return this.props.children({
      data: this.state,
      createEvent: this.createEvent,
    })
  }
}

const timeframe = (block: any) => block.end.diff(block.start, TIME.MILLISECONDS)

const isAvailable = (events: any) => {
  const now = new Date()
  const tenMin = add(now, { minutes: 10 })
  let open: any = [now, add(now, { hours: 2 })]

  const minTimeframe: any = 900000 // 15 minutes

  for (const event of events) {
    const start = parseISO(event.start) // Assuming event.start is in ISO format
    const end = parseISO(event.end) // Assuming event.end is in ISO format
    const range = [start, end]

    // cut out reserved ranges
    open = open.reduce((acc: any, r: any) => {
      const subtractedRanges = range.map((eventRange: any) => {
        const overlapStart = isAfter(r, eventRange[0]) ? r : eventRange[0]
        const overlapEnd = isBefore(add(r, minTimeframe), eventRange[1])
          ? add(r, minTimeframe)
          : eventRange[1]
        return overlapStart < overlapEnd ? [overlapStart, overlapEnd] : null
      })

      return acc.concat(subtractedRanges.filter(Boolean))
    }, [])
  }

  // find a block of time that contains now
  let state: any
  let block: any =
    (isAfter(now, open[0]) || DateUtil().isSame(open[0], TIME.DAYS)) && isBefore(now, open[1])

  // if no block found containing now (currently occupied)
  if (!block) {
    // if available in ten minutes
    if ((block = isAfter(tenMin, open[0]) && isBefore(tenMin, open[1]))) {
      state = {
        status: 'available',
        in: differenceInMilliseconds(open[0], now),
        timeframe: differenceInMilliseconds(open[1], open[0]),
      }
      // if available for at least minimum amount
      if (state.timeframe > minTimeframe) return state
    }
    block = isAfter(open[0], tenMin) && differenceInMilliseconds(open[1], open[0]) > minTimeframe

    // not available
    return block
      ? {
          status: 'occupied',
          timeframe: differenceInMilliseconds(open[1], open[0]),
          for: differenceInMilliseconds(open[0], now),
        }
      : { status: 'occupied', timeframe: 0 }
  }

  // if available for at least minimum amount
  state = {
    status: 'available',
    in: differenceInMilliseconds(open[0], open[1]),
    timeframe: differenceInMilliseconds(open[1], open[0]),
  }
  if (state.in <= 0) delete state.in
  if (state.timeframe > minTimeframe) return state
  // the room is not available
  return { status: 'occupied', timeframe: 0 }
}
